import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, ArrowRight, Loader2 } from "lucide-react";
import { api } from "../api/client";
import { useAppData } from "../data/AppDataContext";
import { naira, formatDate } from "../data/format";
import { useAuth } from "../auth/AuthContext";
import StatusChip from "../components/StatusChip";
import Avatar from "../components/Avatar";
import AccountTypeTag from "../components/AccountTypeTag";

const PROGRESS_COLOR = {
  completed: "var(--green-600)",
  overdue: "var(--red-600)",
  on_track: "var(--blue-600)",
};

const ACCOUNT_TYPE_OPTIONS = [
  { value: "", label: "All account types" },
  { value: "customer_only", label: "Payment only" },
  { value: "rtk_active", label: "RTK active" },
  { value: "portal_user", label: "CORS user" },
];

function CustomerCardSkeleton() {
  return (
    <div className="pl-cust-card" style={{ pointerEvents: "none" }}>
      <div className="pl-cust-card-top">
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--grey-100, #f1f3f8)" }} />
        <div style={{ flex: 1 }}>
          <div style={{ height: 14, width: "60%", background: "var(--grey-100, #f1f3f8)", borderRadius: 4, marginBottom: 6 }} />
          <div style={{ height: 11, width: "40%", background: "var(--grey-100, #f1f3f8)", borderRadius: 4 }} />
        </div>
      </div>
      <div style={{ height: 11, width: "50%", background: "var(--grey-100, #f1f3f8)", borderRadius: 4, margin: "10px 0" }} />
      <div className="pl-cust-progress-track"><div style={{ width: "0%" }} /></div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
        <div style={{ height: 20, width: 70, background: "var(--grey-100, #f1f3f8)", borderRadius: 4 }} />
        <div style={{ height: 20, width: 70, background: "var(--grey-100, #f1f3f8)", borderRadius: 4 }} />
      </div>
    </div>
  );
}

function SpinIcon({ size = 14, color = "var(--blue-600)" }) {
  return (
    <Loader2
      size={size}
      color={color}
      style={{ animation: "pl-spin 0.8s linear infinite" }}
    />
  );
}

export default function Customers() {
  const { salesPeople } = useAppData();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canCreate = ["admin", "team_lead", "manager"].includes(user?.role);
  const canFilterBySalesPerson = ["admin", "team_lead", "manager"].includes(user?.role);

  const mustSearchFirst = user?.role === "office";

  const [searching, setSearching] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");
  const [ordering, setOrdering] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [accountType, setAccountType] = useState("");
  const [salesPersonId, setSalesPersonId] = useState("");

  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const sentinelRef = useRef(null);
  const isFirstSearchEffect = useRef(true);
  const isFirstFilterEffect = useRef(true);

  const fetchPage = useCallback(
    (pageNum, append, triggerType) => {
      const myRequestId = ++requestIdRef.current;

      if (append) {
        setLoadingMore(true);
      } else if (triggerType === "search") {
        setSearching(true);
        setFiltering(false);
      } else {
        setFiltering(true);
        setSearching(false);
      }

      api
        .customers({
          search,
          ordering: ordering || undefined,
          overdue_only: overdueOnly ? "true" : undefined,
          account_type: accountType || undefined,
          sales_person: salesPersonId || undefined,
          page: pageNum,
          limit: 20,
        })
        .then((data) => {
          if (myRequestId !== requestIdRef.current) return;
          setRows((prev) => (append ? [...prev, ...data.results] : data.results));
          setHasMore(data.has_more);
          setError("");
        })
        .catch((e) => {
          if (myRequestId !== requestIdRef.current) return;
          setError(e.message);
        })
        .finally(() => {
          if (myRequestId !== requestIdRef.current) return;
          setLoading(false);
          setLoadingMore(false);

          if (triggerType === "search") {
            setSearching(false);
          } else {
            setFiltering(false);
          }

          setIsInitialLoad(false);
        });
    },
    [search, ordering, overdueOnly, accountType, salesPersonId]
  );

  // Initial page load triggers the "Updating results..." overlay
  useEffect(() => {
    if (mustSearchFirst) {
      setLoading(false);
      return;
    }
    fetchPage(1, false, "initial");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search input triggers only the searchbar spinner (debounced)
  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return;
    }

    clearTimeout(debounceRef.current);

    if (mustSearchFirst && !search.trim()) {
      setRows([]);
      setSearching(false);
      setLoading(false);
      return;
    }

    setSearching(true);
    setFiltering(false);

    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPage(1, false, "search");
    }, 300);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);


  // Filters trigger the "Updating results..." overlay
    useEffect(() => {
      if (isFirstFilterEffect.current) {
        isFirstFilterEffect.current = false;
        return;
      }
      if (mustSearchFirst && !search.trim()) {
        return;
      }

      clearTimeout(debounceRef.current);

      setSearching(false);
      setPage(1);
      fetchPage(1, false, "filter");
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ordering, overdueOnly, accountType, salesPersonId]);

  // Infinite scroll listener
  useEffect(() => {
  if (!sentinelRef.current) return;
  const observer = new IntersectionObserver(
    (entries) => {
      if (
        entries[0].isIntersecting &&
        hasMore &&
        !loading &&
        !loadingMore &&
        !searching &&
        !filtering &&
        !(mustSearchFirst && !search.trim())
      ) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchPage(nextPage, true, "append");
      }
    },
    { rootMargin: "200px" }
  );
  observer.observe(sentinelRef.current);
  return () => observer.disconnect();
}, [hasMore, loading, loadingMore, searching, filtering, page, fetchPage, mustSearchFirst, search]);

  return (
    <div>
      <style>{`
        @keyframes pl-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="pl-page-header-row">
        <div>
          <div className="pl-page-title">Customers</div>
          <div className="pl-page-sub" style={{ marginBottom: 0 }}>Every customer and the plans they're on.</div>
        </div>
        {canCreate && (
          <Link to="/new-plan" className="pl-btn pl-btn-confirm">
            <Plus size={14} /> New customer + plan
          </Link>
        )}
      </div>

      <div className="pl-customers-toolbar" style={{ marginTop: 22, flexWrap: "wrap", gap: 10 }}>
        <div className="pl-search-row">
          <div className="pl-searchbar" style={{ marginBottom: 0, position: "relative", display: "flex", alignItems: "center" }}>
            <Search size={14} color="var(--ink-400)" />
            <input
              placeholder="Search name, phone, email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingRight: searching ? 30 : undefined }}
            />
            {searching && (
              <div style={{ position: "absolute", right: 10, display: "flex", alignItems: "center" }}>
                <SpinIcon size={14} />
              </div>
            )}
          </div>
        </div>
          
        {!mustSearchFirst && (
          <select value={ordering} onChange={(e) => setOrdering(e.target.value)} className="pl-toolbar-select">
            <option value="">Sort: Default</option>
            <option value="balance_desc">Balance - highest first</option>
            <option value="plan_count_desc">Most plans</option>
            <option value="recent">Recently added</option>
          </select>
        )}
        {!mustSearchFirst && (
          <select value={accountType} onChange={(e) => setAccountType(e.target.value)} className="pl-toolbar-select">
            {ACCOUNT_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}

        {canFilterBySalesPerson && (
          <select value={salesPersonId} onChange={(e) => setSalesPersonId(e.target.value)} className="pl-toolbar-select">
            <option value="">All sales people</option>
            {salesPeople.map((sp) => (
              <option key={sp.id} value={sp.id}>{sp.name}</option>
            ))}
          </select>
        )}

        <label className="pl-overdue-toggle">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue only
        </label>
      </div>

      {loading && rows.length === 0 && (
        <div className="pl-cust-grid" style={{ marginTop: 20 }}>
          {Array.from({ length: 8 }).map((_, i) => <CustomerCardSkeleton key={i} />)}
        </div>
      )}

      {error && <div className="pl-empty">{error}</div>}

        {!loading && rows.length === 0 && !error && !searching && !filtering && (
    <div className="pl-empty">
      {mustSearchFirst && !search.trim() ? (
        "Search by name, phone, or email to see a customer's details."
      ) : search ? (
        canCreate ? (
          <>No customer matches "{search}" - <Link to="/new-plan" className="pl-btn-link">create new?</Link></>
        ) : (
          `No customer matches "${search}".`
        )
      ) : (
        "No customers yet."
      )}
    </div>
  )}

      {rows.length > 0 && (
        <div style={{ position: "relative", marginTop: 20 }}>
          {/* Grid overlay shown during initial page load and filter changes */}
          {filtering && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255, 255, 255, 0.55)",
                zIndex: 10,
                borderRadius: 8,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                paddingTop: 40,
                backdropFilter: "blur(1px)",
                transition: "opacity 0.2s ease-in-out",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 16px",
                  background: "#ffffff",
                  borderRadius: 20,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--ink-700, #334155)",
                }}
              >
                <SpinIcon size={16} /> Updating results...
              </div>
            </div>
          )}

          <div className="pl-cust-grid">
            {rows.map((c) => (
              <div className="pl-cust-card" key={c.id} onClick={() => navigate(`/customers/${c.id}`)}>
                <div className="pl-cust-card-top">
                  <Avatar name={c.name} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="pl-cust-card-name">{c.name}</div>
                    <div className="pl-cust-card-meta">{c.phone || c.email || "-"}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    <StatusChip status={c.status} />
                    <AccountTypeTag type={c.account_type} />
                  </div>
                </div>

                <div className="pl-cust-card-meta" style={{ marginBottom: 2 }}>
                  {c.plan_count} plan{c.plan_count === 1 ? "" : "s"} - {c.progress}% complete
                </div>
                <div className="pl-cust-progress-track">
                  <div
                    className="pl-cust-progress-fill"
                    style={{ width: `${c.progress}%`, background: PROGRESS_COLOR[c.status] || "var(--blue-600)" }}
                  />
                </div>

                <div className="pl-cust-card-figures">
                  <div>
                    <div className="pl-cust-card-meta">Outstanding</div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: "var(--red-700)" }}>{naira(c.total_balance)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="pl-cust-card-meta">Last payment</div>
                    <div style={{ fontWeight: 600, fontSize: 12.5 }}>{c.last_payment_date ? formatDate(c.last_payment_date) : "-"}</div>
                  </div>
                </div>

                <button
                  className="pl-btn pl-btn-ghost"
                  style={{ width: "100%", justifyContent: "center", marginTop: 14 }}
                  onClick={(e) => { e.stopPropagation(); navigate(`/customers/${c.id}`); }}
                >
                  Open profile <ArrowRight size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loadingMore && (
        <div className="pl-cust-grid" style={{ marginTop: 14 }}>
          {Array.from({ length: 4 }).map((_, i) => <CustomerCardSkeleton key={`more-${i}`} />)}
        </div>
      )}

      <div ref={sentinelRef} style={{ height: 1 }} />
    </div>
  );
}
